import React from 'react';
import {Divider, Stack, Typography} from '@mui/material';
import {DataId, StyleSx, Sx} from 'app/types/common';
import {PayAsYouGoBilling} from 'app/components/entities/billing/payasyougo';
import {packSx} from 'app/util/packSx/packSx';
import {
  BaseBillingApi,
  HistoryItem,
  InvoiceAmount,
  InvoiceDate,
  InvoiceNumber,
} from 'app/components/entities/billing/base';

interface Props extends Sx {
  invoices: PayAsYouGoBilling.ArchiveInvoice[];
  teamId: string;
  showUnify: boolean;
}

export function InvoicesArchive({sx, invoices, teamId, showUnify}: Props) {
  const hasEdge = invoices.some((invoice) => invoice.tier_usage_paid_usd > 0);

  return (
    <Stack divider={<Divider orientation="horizontal" />} sx={sx}>
      <Header showUnify={showUnify} showEdge={hasEdge} />

      {invoices.map((invoice) => {
        return (
          <ArchiveItem
            dataId={`pay-as-you-go-invoice-${invoice.invoice_id}`}
            key={invoice.invoice_id}
            invoice={invoice}
            teamId={teamId}
            showUnify={showUnify}
            showEdge={hasEdge}
          />
        );
      })}
    </Stack>
  );
}

function getGridStyles(edge: boolean, unify: boolean): StyleSx {
  const columns = 4 + (edge ? 1 : 0) + (unify ? 1 : 0);

  return {
    display: 'grid',
    gap: 1,
    gridTemplateColumns: `100px 1fr repeat(${columns}, minmax(100px, 120px))`,
  };
}

interface HeaderProps {
  showUnify: boolean;
  showEdge: boolean;
}

function Header({showUnify, showEdge}: HeaderProps) {
  return (
    <HistoryItem sx={packSx(getGridStyles(showEdge, showUnify), {fontWeight: 600})}>
      <Typography variant="inherit">Date</Typography>

      <Typography variant="inherit">Number</Typography>

      {showEdge && (
        <Typography variant="inherit" justifySelf="end">
          Edge
        </Typography>
      )}

      <Typography variant="inherit" justifySelf="end">
        LiveScrypt
      </Typography>

      {showUnify && (
        <Typography variant="inherit" justifySelf="end">
          Unify
        </Typography>
      )}

      <Typography variant="inherit" justifySelf="end">
        Total
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Paid
      </Typography>

      <Typography variant="inherit" justifySelf="end">
        Due
      </Typography>
    </HistoryItem>
  );
}

interface ItemProps extends DataId {
  invoice: PayAsYouGoBilling.ArchiveInvoice;
  teamId: string;
  showUnify: boolean;
  showEdge: boolean;
}

function ArchiveItem({dataId, invoice, teamId, showUnify, showEdge}: ItemProps) {
  return (
    <HistoryItem dataId={dataId} sx={getGridStyles(showEdge, showUnify)}>
      <InvoiceDate dataId="invoice-date" timestamp={invoice.issued_at} />

      <InvoiceNumber
        dataId="invoice-number"
        number={invoice.invoice_number}
        href={
          invoice.available_for_download
            ? BaseBillingApi.getInvoiceUrl(teamId, invoice.invoice_id)
            : undefined
        }
      />

      {showEdge && <InvoiceAmount dataId="invoice-total" amount={invoice.tier_usage_paid_usd} />}

      <InvoiceAmount dataId="invoice-lvs" amount={invoice.transcriber_paid_usd} />

      {showUnify && <InvoiceAmount dataId="invoice-unify" amount={invoice.unify_paid_usd} />}

      <InvoiceAmount dataId="invoice-total" amount={invoice.total_amount_usd} />

      <InvoiceAmount dataId="invoice-paid" amount={invoice.amount_paid_usd} />

      <InvoiceAmount dataId="invoice-due" amount={invoice.amount_remaining_usd} />
    </HistoryItem>
  );
}
